import React, { Ref, useContext, useEffect, useMemo, useState } from "react";

import {
  cartIsValid,
  getTotalPrice,
  generateTopLevelItem,
  choiceRequiredForProduct,
} from "~/helpers/cartHelpers";
import Button from "~/components/Button/Button";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import Modifiers from "../../components/Modifiers/Modifiers";
import PlusIcon from "~/icons/PlusIcon";
import MinusIcon from "~/icons/MinusIcon";
import TaxContext from "~/contexts/TaxContext";

import styles from "./ProductModal.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import {
  ProductModalInfoAllergensFontSize,
  ProductModalInfoPriceFontSize,
  ProductModalInfoTitleFontSize,
} from "~/theme/models/themeModels";
import ProductDescription from "../../components/ProductDescription/ProductDescription";
import AlternateProductSelection from "../../components/AlternateProductSelection/AlternateProductSelection";
import clsx from "clsx";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import { hexColorToRGBA } from "~/helpers/colorHelpers";
import BackButton from "~/components/BackButton/BackButton";
import { useWindowDimensions } from "~/hooks/useWindowDimensions";
import ModalCloseIcon from "~/icons/ModalCloseIcon";
import { LABELS } from "~/modules/checkout/constants";
import { CartLine } from "~/entities/orderEntities";
import { Product } from "~/entities/productEntities";
import InfoIcon from "~/icons/InfoIcon";
import SubModifiersDrawer from "../../components/SubModifiersDrawer/SubModifiersDrawer";
import { useMediaQuery } from "~/hooks/useMediaQuery";

type ProductModalProps = {
  initLine: CartLine | null;
  onClose: VoidFunction;
  isInsert: boolean;
  isForPromoModal?: boolean;
  backdropRef?: Ref<HTMLDivElement>;
  // Deal builder: return the configured line instead of adding it to the cart
  onSave?: (line: CartLine) => void;
  saveLabel?: (line: CartLine) => string;
  // Shown under the product info, e.g. the "Make it a combo" prompt, given the line as currently configured
  extraContent?: (line: CartLine, close: VoidFunction) => React.ReactNode;
};

const ProductModal: React.FC<ProductModalProps> = ({
  initLine,
  onClose: onCloseParam,
  isInsert,
  isForPromoModal,
  backdropRef,
  onSave,
  saveLabel,
  extraContent,
}) => {
  // nav is an array of paths to the currently viewed cart item
  // the last item in nav is the path to the cart item we are modifying via child ids
  // e.g. last item of ["123", "1234"] means we are currently viewing cart.children.find((x) => x.id === "123").children.find((x) => x.id === "1234")
  // the first item in the array is [] meaning just show the root cart item.
  const [nav, setNav] = useState<string[][]>([[]]);
  const [cart, setCart] = useState<CartLine | null>(null);
  const [showError, setShowError] = useState(false);

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Reset navigation when the modal is closed
  const onClose = () => {
    setNav([[]]);
    onCloseParam();
  };

  const { addOrderLine, updateOrderLine, orderSource, readonlyCart } =
    useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const { width } = useWindowDimensions();

  // Get selections for the original product (preserved when switching)
  const prodOptions = useMemo(() => {
    if (!initLine || !menu) {
      return [];
    }

    const originalMenuItem = menu?.tabs
      .flatMap((tab) => tab.items)
      .find((item) => item.code === initLine.menuItemCode);
    if (!originalMenuItem) {
      return [];
    }

    return [
      originalMenuItem.productData,
      ...originalMenuItem.alternateProducts,
    ];
  }, [initLine, menu]);
  const showAlternativeProductSelections = prodOptions.length >= 2;
  const hasModifierSelection =
    !!cart && choiceRequiredForProduct(cart.item.product, orderSource);
  const showOptionsSection =
    hasModifierSelection || showAlternativeProductSelections;

  useEffect(() => {
    if (initLine) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";

      // Reset hover state for all buttons (Safari)
      const buttons = document.querySelectorAll("button");

      buttons.forEach((button) => {
        button.style.pointerEvents = "none";

        setTimeout(() => {
          button.style.pointerEvents = "";
        }, 0);
      });
    }
  }, [initLine, width]);

  useEffect(() => {
    if (showError && cart && cartIsValid(cart.item)) {
      setShowError(false);
    }
  }, [cart, showError]);

  useEffect(() => {
    if (initLine) {
      setCart(initLine);
    } else {
      setCart(null);
    }
  }, [initLine]);

  const addToCart = () => {
    if (cart) {
      if (!cartIsValid(cart.item)) {
        return setShowError(true);
      }

      if (onSave) {
        onSave(cart);
      } else if (isInsert) {
        addOrderLine(cart);
      } else {
        updateOrderLine(cart);
      }
      onClose();
    }
  };

  const isSubOptions = nav.length > 1;

  // Styles
  const backgroundColor = template.productScreen.general.backgroundColour;
  const sectionBackgroundColor =
    template.productScreen.general.sectionBackgroundColour;

  const infoImageBackgroundColor =
    template.productScreen.info.imageBackgroundColour;
  const infoTitleFontSize =
    ProductModalInfoTitleFontSize[template.productScreen.info.titleFontSize];
  const infoTitleColor = template.productScreen.info.titleColour;
  const infoShowDescription =
    template.productScreen.info.showDescription && !!cart?.item?.product?.descr;
  const infoPriceFontSize =
    ProductModalInfoPriceFontSize[template.productScreen.info.priceFontSize];
  const infoPriceColor = template.productScreen.info.priceColour;
  // Allergens
  const infoShowAllergens = template.productScreen.info.showAllergens;
  const infoShowAllergensIcon = template.productScreen.info.showAllergensIcon;
  const infoAllergensFontSize =
    ProductModalInfoAllergensFontSize[
      template.productScreen.info.allergensFontSize
    ];
  const infoAllergensFontColor =
    template.productScreen.info.allergensFontColour;
  const infoAllergensLink = template.productScreen.info.allergensLink;
  const allergensLinkColor = template.productScreen.info.allergensLinkColour;

  const cssVariables: Record<string, string> = {
    "--product-modal-section-stroke-color": hexColorToRGBA(
      template.productScreen.general.sectionStrokeColour,
      template.productScreen.general.sectionStroke ? 0.25 : 0,
    ),
  };

  const qty = cart?.item?.qty ?? 1;
  const setQty = (x: number) => {
    if (cart) {
      setCart({
        ...cart,
        item: {
          ...cart.item,
          qty: x,
        },
      });
    }
  };

  const handleAlternativeProductSelect = (product: Product) => {
    if (cart) {
      setCart({
        ...cart,
        item: generateTopLevelItem(product),
      });
    }
  };

  return initLine && cart ? (
    <div
      ref={backdropRef}
      className={clsx(
        styles.backdrop,
        isForPromoModal && styles.showAbovePromoModal,
      )}
    >
      <SubModifiersDrawer
        cart={cart.item}
        setCart={(x) => setCart({ ...cart, item: x })}
        nav={nav}
        setNav={setNav}
        orderSource={orderSource}
      />
      <div className={styles.modal} style={{ backgroundColor }}>
        <div className={styles.header}>
          <div className={styles.headerButtonWrapper}>
            <BackButton
              text={isForPromoModal ? "Back" : "Back to menu"}
              onClick={onClose}
            />
          </div>
          <div className={styles.closeButton} onClick={onClose}>
            <ModalCloseIcon />
          </div>
        </div>
        <div className={styles.scrollWrapper}>
          <div className={styles.content} style={cssVariables}>
            <div
              className={clsx(
                styles.productSection,
                !showOptionsSection && styles.productSectionCenter,
              )}
              style={{ backgroundColor: sectionBackgroundColor }}
            >
              {cart?.item?.product?.image && (
                <div
                  className={styles.productImageWrapper}
                  style={{ backgroundColor: infoImageBackgroundColor }}
                >
                  <img
                    className={styles.productImage}
                    src={uploadcareConfig.getUrl(
                      cart.item?.product.image.fileId,
                      cart.item?.product.image.modifiers,
                    )}
                    alt={cart.item?.product.image.fileName}
                  />
                </div>
              )}
              <div className={styles.productInfo}>
                <span
                  className={styles.productInfoName}
                  style={{
                    fontSize: infoTitleFontSize,
                    color: infoTitleColor,
                  }}
                >
                  {cart?.item?.product.name || initLine?.item?.product?.name}
                </span>
                <span
                  className={styles.productInfoPrice}
                  style={{
                    fontSize: infoPriceFontSize,
                    color: infoPriceColor,
                  }}
                >
                  {formatAmount(
                    cart?.item?.product.price ||
                      initLine?.item?.product?.price ||
                      0,
                  )}
                </span>
                {infoShowDescription && (
                  <ProductDescription
                    description={
                      cart?.item?.product.descr ||
                      initLine?.item?.product?.descr ||
                      ""
                    }
                  />
                )}
                {infoShowAllergens && (
                  <div
                    className={styles.productInfoAllergens}
                    style={{
                      fontSize: infoAllergensFontSize,
                      color: infoAllergensFontColor,
                    }}
                  >
                    {infoShowAllergensIcon && <InfoIcon color="#FFA20C" />}
                    For allergen information{" "}
                    <a
                      href={infoAllergensLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: allergensLinkColor }}
                    >
                      click here
                    </a>
                  </div>
                )}
                {extraContent?.(cart, onClose)}
              </div>
            </div>
            {showOptionsSection && (
              <div
                className={styles.productParamsSection}
                style={{ backgroundColor: sectionBackgroundColor }}
              >
                {showAlternativeProductSelections &&
                  (nav.length < 2 || isMobile) && (
                    <AlternateProductSelection
                      prodOptions={prodOptions}
                      selectedProductCode={cart.item.productCode}
                      onProductSelect={handleAlternativeProductSelect}
                    />
                  )}
                {cart && (
                  <Modifiers
                    cart={cart.item}
                    setCart={(x) => setCart({ ...cart, item: x })}
                    nav={isMobile ? [[]] : nav}
                    setNav={setNav}
                    showError={showError}
                    orderSource={orderSource}
                  />
                )}
                {showError && (
                  <div className={styles.error}>
                    Please correct any options marked as red
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(!isSubOptions || isMobile) && (
          <div className={styles.footer}>
            <div className={styles.footerActionsWrapper}>
              <div
                className={clsx(
                  styles.footerActions,
                  styles.footerActionsCenter,
                  // No quantity when choosing for the deal builder
                  onSave && styles.footerActionsSingle,
                )}
              >
                {!onSave && (
                  <div className={styles.counterWrapper}>
                    <Button
                      variant="counter-secondary"
                      onClick={() => setQty(Math.max(qty - 1, 1))}
                      disabled={qty === 1}
                    >
                      <MinusIcon />
                    </Button>
                    <span>{cart?.item?.qty}</span>
                    <Button
                      variant="counter-primary"
                      onClick={() => setQty(qty + 1)}
                      disabled={!!cart && cart.item.product.soldOut}
                    >
                      <PlusIcon />
                    </Button>
                  </div>
                )}
                <Button onClick={addToCart} disabled={readonlyCart}>
                  {onSave && saveLabel ? (
                    <span className={styles.buttonLabel}>
                      {saveLabel(cart)}
                    </span>
                  ) : (
                    <span className={styles.buttonLabel}>
                      <span>
                        {isInsert ? LABELS.ADD_TO_CART : "Update order"} -
                      </span>
                      <span>
                        {formatAmount(cart ? getTotalPrice(cart.item) : 0)}
                      </span>
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;
};

export default ProductModal;
